import '@tenant/styles/index.css';
import { FC, lazy, PropsWithChildren, Suspense } from 'react';
import { GlobalStyle } from 'src/common/styles/GlobalStyle';
import { BrowserRouter, MemoryRouter, Route, Switch } from 'react-router-dom';
import {
  AspspSelectPageRoute,
  ContactPageRoute,
  DefinitionPageRoute,
  LoaderPageRoute,
  MainPageRoute, SmartFlowPageRoute,
  VerificationPageRoute,
} from 'src/routes';
import 'react-toastify/dist/ReactToastify.css';
import { CustomToastContainer } from 'src/common/components/Toast/CustomToastContainer';
import { ScrollToTop } from 'src/utils/scrollToTop';
import { RequireCorrectStep } from 'src/common/components/RequireCorrectStep';
import { ReactQueryClientProvider } from 'src/config/ReactQueryClientProvider';
import { customizeZodErrorMap } from 'src/config/customizeZodErrorMap';
import { environment } from 'src/config/environment';
import { AppIntlProvider } from 'src/config/intl/AppIntlProvider';
import styled, { ThemeProvider } from 'styled-components';
import { Spinner } from 'src/common/components/Spinner';
import MainPage from 'src/pages/MainPage';
import AspspSelectPage from 'src/pages/AspspSelectPage';
import { theme } from './common/styles/theme';

const FallbackContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 17.5rem;
`;

const Fallback = () => (
  <FallbackContainer>
    <Spinner />
  </FallbackContainer>
);

customizeZodErrorMap();

const baseRoute = './pages';

const VerificationPage = lazy(() => import(`${baseRoute}/VerificationPage`));
const ContactPage = lazy(() => import(`${baseRoute}/ContactPage`));
const DefinitionPage = lazy(() => import(`${baseRoute}/DefinitionPage`));
const LoaderPage = lazy(() => import(`${baseRoute}/LoaderPage`));
const NotFoundPage = lazy(() => import(`${baseRoute}/NotFoundPage`));
const DevComponentsPage = lazy(() => import(`${baseRoute}/DevComponentsPage`));
const SmartFlowPage = lazy(() => import(`${baseRoute}/SmartFlowPage`));

const Router: FC<PropsWithChildren> = ({ children }) => {
  if (environment.isWebSdk) {
    return <MemoryRouter>{children}</MemoryRouter>;
  }

  return (
    <BrowserRouter basename={environment.routerBaseName}>
      {children}
    </BrowserRouter>
  );
};

function App() {
  return (
    <ReactQueryClientProvider>
      <AppIntlProvider>
        <ThemeProvider theme={theme}>
          <GlobalStyle />
          <Suspense fallback={<Fallback />}>
            <Router>
              <CustomToastContainer />
              <ScrollToTop />
              <Switch>
                <Route
                  path={MainPageRoute}
                  render={() => (
                    <RequireCorrectStep>
                      <MainPage />
                    </RequireCorrectStep>
                  )}
                  exact
                />
                <Route
                  path={AspspSelectPageRoute}
                  render={() => (
                    <RequireCorrectStep>
                      <AspspSelectPage />
                    </RequireCorrectStep>
                  )}
                />
                <Route
                  path={VerificationPageRoute}
                  component={VerificationPage}
                />
                <Route path={ContactPageRoute} component={ContactPage} />
                <Route path={DefinitionPageRoute} component={DefinitionPage} />
                <Route path={LoaderPageRoute} component={LoaderPage} />
                <Route path={SmartFlowPageRoute} component={SmartFlowPage} />

                {environment.isDevelopment && (
                  <Route
                    path="/DevComponentsPage"
                    component={DevComponentsPage}
                  />
                )}

                <Route path="*" component={NotFoundPage} />
              </Switch>
            </Router>
          </Suspense>
        </ThemeProvider>
      </AppIntlProvider>
    </ReactQueryClientProvider>
  );
}

export default App;
